import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import {
  ACCOUNT_GEM_GROUPS,
  ACCOUNT_GEM_LADDER,
  ACCOUNT_GEM_ME,
  ACCOUNT_GEM_OPEN_COMMISSION,
  ACCOUNT_GEM_OPEN_INVOICES,
} from './accountFixture';
import { AccountPageShell } from './AccountPageShell';
import { Grid, Row, SmallButton } from './primitives';

/**
 * AR-380 — GEM Status.
 *
 * v15 moved GEM into the Account center as a page of its own (D-ACCT-26); the
 * summary panel that used to sit beside Personal + Security and Business +
 * Brand is gone, because it was a summary of the page one row down the column.
 *
 * The gauge carries TWO numbers: what has been paid, and what is projected once
 * the open invoices clear. The projected arc is drawn first and the paid arc
 * over it, so the lighter band shows only as the increment — and because that
 * increment can be a sliver, the projected figure is printed rather than left
 * to the drawing.
 *
 * Binding these values to finance is a separate ticket; the front end is this
 * one's (see the ticket's "Not in this ticket").
 */

type GemView = 'paid' | 'open';

const money = (n: number) => `$${Number(n).toLocaleString('en-US')}`;

const rankOf = (level: string) => ACCOUNT_GEM_LADDER.findIndex(([n]) => n === level);
const thresholdAt = (level: string) => ACCOUNT_GEM_LADDER[rankOf(level)]?.[1];
const groupOf = (level: string) =>
  ACCOUNT_GEM_GROUPS.find((g) => g.levels.some(([n]) => n === level))?.group ?? '';
const nextLevel = () => ACCOUNT_GEM_LADDER[rankOf(ACCOUNT_GEM_ME.level) + 1]?.[0];

/** What stands between her and a level. null when the level has no dollar threshold. */
const gapTo = (level?: string) => {
  if (!level) return null;
  const at = thresholdAt(level);
  return typeof at === 'number' && at > ACCOUNT_GEM_ME.ytd ? at - ACCOUNT_GEM_ME.ytd : null;
};

export const AccountGem: React.FC = () => {
  const [view, setView] = useState<GemView>('paid');

  const me = ACCOUNT_GEM_ME;
  const next = nextLevel();
  const group = groupOf(me.level);
  const target = next ? thresholdAt(next) : undefined;
  const projected = me.ytd + ACCOUNT_GEM_OPEN_COMMISSION;

  const pct = typeof target === 'number' ? Math.min(100, (me.ytd / target) * 100) : 100;
  const projPct =
    typeof target === 'number' ? Math.min(100, (projected / target) * 100) : 100;

  const showProjection = view === 'open';
  // Does the open work carry her past the next level, or only close the gap?
  const clears = showProjection && typeof target === 'number' && projected >= target;
  const paidGap = gapTo(next);
  const projGap =
    typeof target === 'number' && target > projected ? target - projected : null;

  const ARC = 'M69.3 188.7 A100 100 0 1 1 210.7 188.7';

  const header = (
    <span className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
      {(
        [
          ['paid', 'Paid'],
          ['open', 'Paid + open'],
        ] as const
      ).map(([id, label]) => (
        <button
          key={id}
          type="button"
          aria-pressed={view === id}
          onClick={() => setView(id)}
          className={cn(
            'rounded-md px-3 py-1 text-[12.5px] font-medium',
            view === id
              ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
              : 'text-gray-600 dark:text-gray-300',
          )}
        >
          {label}
        </button>
      ))}
    </span>
  );

  return (
    <AccountPageShell page="gem" headerActions={header}>
      <Grid cols={2}>
        {/* 1 · the gauge */}
        <div
          data-gem-gauge
          className="rounded-lg border border-gray-200 bg-white px-6 pb-4 pt-5 text-center dark:border-gray-700 dark:bg-[#1A2021]"
        >
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
            {group}
          </div>
          <div className="text-[25px] font-bold text-gray-900 dark:text-gray-100">
            {me.level}
          </div>

          {clears ? (
            <div className="mt-2 rounded-lg bg-green-50 px-3 py-2 text-left text-[13.5px] dark:bg-green-900/20">
              <b className="block text-gray-900 dark:text-gray-100">
                You&apos;re on track for {next}!
              </b>
              <small className="text-gray-600 dark:text-gray-400">
                You have {money(ACCOUNT_GEM_OPEN_COMMISSION)} of commission outstanding on
                open (unpaid) invoices. Once those are paid you will reach {next}.
              </small>
            </div>
          ) : null}

          <div className="relative mx-auto mt-1 w-[min(300px,100%)]">
            <svg viewBox="0 0 280 204" className="block w-full" role="img"
              aria-label={`${money(me.ytd)} of qualifying revenue paid${next ? `, towards ${next}` : ''}`}>
              <path d={ARC} fill="none" strokeWidth="15" strokeLinecap="round" className="stroke-gray-200 dark:stroke-gray-700" />
              {showProjection ? (
                <path
                  d={ARC}
                  fill="none"
                  strokeWidth="15"
                  strokeLinecap="round"
                  pathLength={100}
                  strokeDasharray={`${projPct.toFixed(1)} 100`}
                  className="stroke-orange-300"
                />
              ) : null}
              <path
                d={ARC}
                fill="none"
                strokeWidth="15"
                strokeLinecap="round"
                pathLength={100}
                strokeDasharray={`${pct.toFixed(1)} 100`}
                className="stroke-orange-500"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center pt-2.5">
              <b className="text-[33px] leading-none text-gray-900 dark:text-gray-100">
                {money(me.ytd)}
              </b>
              <small className="mt-1 text-[14px] text-gray-500 dark:text-gray-400">
                qualifying revenue paid
              </small>
              {showProjection ? (
                <>
                  <i className="my-2 block w-[118px] border-t border-gray-200 dark:border-gray-700" />
                  <b className="text-[17px] font-semibold leading-none text-gray-700 dark:text-gray-300">
                    {money(projected)} projected
                  </b>
                  <small className="mt-1 max-w-[172px] text-[12px] leading-snug text-gray-600 dark:text-gray-400">
                    with commission on {ACCOUNT_GEM_OPEN_INVOICES} open invoices
                  </small>
                </>
              ) : null}
            </div>
            <span className="absolute bottom-2 left-0 text-[12px] text-gray-500 dark:text-gray-400">
              $0
            </span>
            {next && typeof target === 'number' ? (
              <span className="absolute bottom-2 right-0 text-right text-[12px] text-gray-500 dark:text-gray-400">
                <b className="block text-gray-900 dark:text-gray-100">{next}</b>
                {money(target)}
              </span>
            ) : null}
          </div>

          {/* One line per row: the arithmetic, then where it lands. */}
          {showProjection ? (
            <div className="mt-2.5 text-[16.5px] leading-snug text-gray-700 dark:text-gray-300">
              <span className="text-[14px] text-gray-500 dark:text-gray-400">
                {money(me.ytd)} paid + {money(ACCOUNT_GEM_OPEN_COMMISSION)} open ={' '}
                <b className="text-gray-900 dark:text-gray-100">{money(projected)}</b>
              </span>
              <div className="mt-1">
                {projGap === null ? (
                  <>
                    Enough to reach <b className="text-gray-900 dark:text-gray-100">{next}</b>
                  </>
                ) : (
                  <>
                    <b className="text-gray-900 dark:text-gray-100">{money(projGap)}</b>{' '}
                    short of {next} once the open invoices are paid
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-2.5 text-[16.5px] leading-snug text-gray-700 dark:text-gray-300">
              {paidGap === null ? (
                <>
                  You have reached{' '}
                  <b className="text-gray-900 dark:text-gray-100">{next ?? me.level}</b>
                </>
              ) : (
                <>
                  <b className="text-gray-900 dark:text-gray-100">{money(paidGap)}</b> to{' '}
                  {next}
                </>
              )}
            </div>
          )}

          {/* Paid carries no legend at all — one arc, one figure. */}
          {showProjection ? (
            <div className="mt-2.5 flex flex-wrap items-center justify-center gap-3.5 text-[12.5px] text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <i className="h-2 w-2 rounded-full bg-orange-500" />
                paid
              </span>
              <span className="flex items-center gap-1.5">
                <i className="h-2 w-2 rounded-full bg-orange-300" />
                projected
              </span>
              {clears ? null : (
                <span className="flex items-center gap-1.5">
                  <i className="h-2 w-2 rounded-full bg-gray-200 dark:bg-gray-700" />
                  to {next}
                </span>
              )}
            </div>
          ) : null}

          <small className="mt-2 block text-[13px] text-gray-500 dark:text-gray-400">
            Your {new Date().getFullYear()} qualifying revenue sets your{' '}
            {new Date().getFullYear() + 1} status — reach a level and you hold it through
            next year
          </small>
        </div>

        {/* 2 · where she stands on the ladder */}
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
              GEM progression
            </span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              level {me.lvlNo} of {ACCOUNT_GEM_LADDER.length}
            </span>
          </div>

          <div className="mb-3 flex gap-[3px]">
            {ACCOUNT_GEM_LADDER.map(([name], i) => {
              const reached = i <= rankOf(me.level);
              const projectedReach =
                showProjection &&
                !reached &&
                typeof ACCOUNT_GEM_LADDER[i][1] === 'number' &&
                (ACCOUNT_GEM_LADDER[i][1] as number) <= projected;
              return (
                <i
                  key={name}
                  title={name}
                  className={cn(
                    'h-1.5 flex-1 rounded-full',
                    reached
                      ? 'bg-orange-500'
                      : projectedReach
                        ? 'bg-orange-300'
                        : 'bg-gray-200 dark:bg-gray-700',
                  )}
                />
              );
            })}
          </div>

          {ACCOUNT_GEM_GROUPS.map((g) => {
            const mine = g.group === group;
            return (
              <div
                key={g.group}
                className={cn(
                  'border-b border-gray-100 py-2.5 last:border-b-0 dark:border-gray-800',
                  mine && 'rounded-md bg-orange-50/60 px-2 dark:bg-orange-900/10',
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <b className="text-[15px] text-gray-900 dark:text-gray-100">{g.group}</b>
                  <small className="text-[13px] text-gray-500 dark:text-gray-400">
                    {g.sub}
                  </small>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[13.5px]">
                  {g.levels.map(([name, at]) => {
                    const reached = rankOf(me.level) >= rankOf(name);
                    return (
                      <span
                        key={name}
                        className={cn(
                          'whitespace-nowrap',
                          name === me.level
                            ? 'font-semibold text-orange-700 dark:text-orange-400'
                            : reached
                              ? 'text-gray-700 dark:text-gray-300'
                              : 'text-gray-500 dark:text-gray-400',
                        )}
                      >
                        {name}
                        <span className="ml-1 text-gray-600 dark:text-gray-400">
                          {typeof at === 'number' ? money(at) : at}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Grid>

      <div className="mt-3.5 rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
        <div className="mb-3">
          <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
            What counts
          </span>
        </div>
        <Row
          label="Qualifying revenue"
          value="Commission on paid invoices, across every booking type, for the calendar year."
        />
        <Row
          label="Open invoices"
          value={`Commission on ${ACCOUNT_GEM_OPEN_INVOICES} invoices that have not been paid yet — shown as the projection, not as your level.`}
        />
        <Row
          label="When it counts"
          value="Your level is set by the calendar year and held through the next one."
          last
        />
        <div className="mt-2.5">
          <SmallButton onClick={() => setView(view === 'paid' ? 'open' : 'paid')}>
            {view === 'paid' ? 'Show the projection' : 'Show paid only'}
          </SmallButton>
        </div>
      </div>
    </AccountPageShell>
  );
};

export default AccountGem;
